import { useState } from "react";
import { useActionRequest, useDataRequest } from "../../common/hooks/api";
import { NetworkRequestState } from "../../common/models";
import { Api } from "../../common/service/Api";
import {
  CreateDataRetentionPolicyDetailDto,
  DataRetentionPolicyDto,
  UpdateDataRetentionPolicyDetailDto,
} from "../../common/service/server/dto/DataRetentionPolicyDto";

import { OrganizationPublicId, UUIDString } from "../../common/service/server/Types";

// organization request
export type UpdateDataRentionPolicyFn = (args: {
  id: UUIDString;
  dto: UpdateDataRetentionPolicyDetailDto;
}) => Promise<void>;

export type CreateDataRentionPolicyFn = (args: {
  dto: CreateDataRetentionPolicyDetailDto;
}) => Promise<void>;

export type RemoveDataRetentionPolicyFn = (args: { id: UUIDString }) => Promise<void>;

export const useDataRetentionPolicy = (
  organizationId: OrganizationPublicId,
): {
  onLoad: boolean;
  result: DataRetentionPolicyDto;
  request: NetworkRequestState;
  refresh: () => void;
  update: UpdateDataRentionPolicyFn;
  updateRequest: NetworkRequestState;
  create: CreateDataRentionPolicyFn;
  createRequest: NetworkRequestState;
  remove: RemoveDataRetentionPolicyFn;
  removeRequest: NetworkRequestState;
} => {
  const [onLoad, setOnLoad] = useState(false);

  const { result, refresh, request } = useDataRequest({
    queryKey: ["dataRetention", organizationId],
    api: async () => {
      return Api.dataRetention.getDataRetention(organizationId);
    },
    onSuccess: () => {
      setOnLoad(true);
    },
  });

  // update request
  const { fetch: update, request: updateRequest } = useActionRequest({
    api: ({ id, dto }: { id: UUIDString; dto: UpdateDataRetentionPolicyDetailDto }) => {
      return Api.dataRetention.updateDataRetentionCategory(organizationId, id, dto);
    },
    onSuccess: () => {
      setOnLoad(false);
      return refresh();
    },
  });

  // create request
  const { fetch: create, request: createRequest } = useActionRequest({
    api: ({ dto }: { dto: CreateDataRetentionPolicyDetailDto }) =>
      Api.dataRetention.createDataRetentionCategory(organizationId, dto),
    onSuccess: () => {
      setOnLoad(false);
      return refresh();
    },
  });

  // remove request
  const { fetch: remove, request: removeRequest } = useActionRequest({
    api: ({ id }: { id: UUIDString }) =>
      Api.dataRetention.removeDataRetentionCategory(organizationId, id),
    onSuccess: () => {
      setOnLoad(false);
      return refresh();
    },
  });

  return {
    onLoad,
    result,
    request,
    refresh,
    update,
    updateRequest,
    create,
    createRequest,
    remove,
    removeRequest,
  };
};
